<template>
  <Teleport to="body">
    <div v-if="state.isOpen" class="listing-detail">
      <div class="listing-detail__scrim" @click="close" />
      <div
        ref="panel"
        class="listing-detail__panel"
        role="dialog"
        aria-modal="true"
        :aria-label="t('listing-detail.aria-label')"
      >
        <div class="listing-detail__media">
          <div
            v-if="images.length"
            ref="track"
            class="listing-detail__slider"
            @scroll.passive="onScroll"
            @pointerdown="stopAutoplay"
          >
            <img
              v-for="(image, index) in images"
              :key="index"
              class="listing-detail__image"
              :src="image"
              alt=""
              :loading="index === 0 ? 'eager' : 'lazy'"
              draggable="false"
            />
          </div>
          <div v-else class="listing-detail__image listing-detail__image--empty">
            <SvgIcon name="home" :size="32" />
            <span class="listing-detail__no-image">{{ t('listing-card.no-image') }}</span>
          </div>

          <button
            ref="closeButton"
            type="button"
            class="listing-detail__close"
            :aria-label="t('listing-detail.close')"
            @click="close"
          >
            <SvgIcon name="x" :size="17" />
          </button>

          <template v-if="images.length > 1">
            <button
              v-if="current > 0"
              type="button"
              class="listing-detail__nav listing-detail__nav--prev"
              :aria-label="t('listing-card.image-prev')"
              @click="goToManually(current - 1)"
            >
              <SvgIcon name="chevron" :size="14" :stroke="2" />
            </button>
            <button
              v-if="current < images.length - 1"
              type="button"
              class="listing-detail__nav listing-detail__nav--next"
              :aria-label="t('listing-card.image-next')"
              @click="goToManually(current + 1)"
            >
              <SvgIcon name="chevron" :size="14" :stroke="2" />
            </button>
            <div class="listing-detail__dots">
              <button
                v-for="(image, index) in images"
                :key="index"
                type="button"
                class="listing-detail__dot"
                :class="{ 'is-active': index === current }"
                :aria-label="t('listing-card.image-goto', { index: index + 1 })"
                @click="goToManually(index)"
              />
            </div>
          </template>
        </div>

        <div v-if="listing.status" class="listing-detail__status-bar" :class="statusModifier">
          {{ listing.status }}
        </div>

        <div class="listing-detail__scroll">
          <div class="listing-detail__body">
            <h2 class="listing-detail__title">{{ title }}</h2>
            <p v-if="subtitle" class="listing-detail__subtitle">{{ subtitle }}</p>

            <p class="listing-detail__price">
              <span v-if="price.prefix" class="listing-detail__price-prefix">{{ price.prefix }}</span>
              <strong class="listing-detail__price-value">{{ price.value }}</strong>
              <span v-if="price.suffix" class="listing-detail__price-suffix">{{ price.suffix }}</span>
            </p>

            <div class="listing-detail__actions">
              <a
                v-if="listing.url"
                class="listing-detail__button listing-detail__button--primary"
                :href="listing.url"
                target="_blank"
                rel="noopener"
              >{{ t('listing-detail.view-home') }}</a>
              <a
                v-if="listing.project && listing.project.url"
                class="listing-detail__button listing-detail__button--secondary"
                :href="listing.project.url"
                target="_blank"
                rel="noopener"
              >{{ t('listing-detail.view-project') }}</a>
            </div>

            <div v-if="specs.length" class="listing-detail__specs" :class="{ 'is-open': specsOpen }">
              <button
                type="button"
                class="listing-detail__specs-toggle"
                :aria-expanded="specsOpen"
                aria-controls="listing-detail-specs"
                @click="specsOpen = !specsOpen"
              >
                <span>{{ t('listing-detail.specs.toggle') }}</span>
                <SvgIcon name="chevron" :size="14" :stroke="2" />
              </button>
              <dl v-if="specsOpen" id="listing-detail-specs" class="listing-detail__specs-list">
                <div v-for="spec in specs" :key="spec.label" class="listing-detail__spec">
                  <dt class="listing-detail__spec-label">
                    <!-- Sprite symbol, the same mechanism and the same icons the
                         construction-number page uses for these specs -->
                    <svg class="listing-detail__spec-icon" aria-hidden="true">
                      <use :href="`/public/img/sprite.svg#${spec.icon}`" />
                    </svg>
                    {{ spec.label }}
                  </dt>
                  <dd class="listing-detail__spec-value">{{ spec.value }}</dd>
                </div>
              </dl>
            </div>

            <!-- eslint-disable-next-line vue/no-v-html : sanitized CMS wysiwyg -->
            <div v-if="descriptionHtml" class="listing-detail__description" v-html="descriptionHtml" />

            <p v-if="state.error" class="listing-detail__error">{{ t('listing-detail.error') }}</p>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import SvgIcon from './SvgIcon.vue';
import { useDetail } from '../lib/useDetail';
import { sanitizeHtml, stripTextColors, openLinksInNewTab } from '../lib/sanitizeHtml';
import { housingTitleWithNumber } from '../lib/housingTitle';
import { t } from '../lib/i18n';

const { state, close } = useDetail();

const priceFormatter = new Intl.NumberFormat('nl-NL');

// The card/popup seed renders immediately; the fetched detail overrides and
// extends it as soon as it lands.
const listing = computed(() => ({ ...(state.seed || {}), ...(state.detail || {}) }));

const images = computed(() =>
  (listing.value.images || []).map((image) => image.url || image.src || image).filter(Boolean)
);

const panel = ref(null);
const closeButton = ref(null);
const track = ref(null);
const current = ref(0);
const specsOpen = ref(false);

const onScroll = () => {
  const el = track.value;
  if (el) current.value = Math.round(el.scrollLeft / el.clientWidth);
};

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const goTo = (index) => {
  const el = track.value;
  if (!el) return;
  // Autoplay never starts under reduced motion, but the prev/next buttons and
  // the dots still animate their jump; those are the same movement.
  el.scrollTo({ left: index * el.clientWidth, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
};

// Autoplay advances the images while the panel sits open (WHN-2144). It wraps
// from the last image back to the first, where the prev/next buttons
// deliberately do not: a timer that stops dead on the last image reads as
// broken, a button that wraps hides where the series ends.
const AUTOPLAY_INTERVAL_MS = 5000;

let autoplayTimer = null;
let autoplayTakenOver = false;

const clearAutoplay = () => {
  if (!autoplayTimer) return;
  window.clearInterval(autoplayTimer);
  autoplayTimer = null;
};

// Systems set to reduced motion never start it at all.
const startAutoplay = () => {
  clearAutoplay();
  if (autoplayTakenOver || images.value.length < 2) return;
  if (prefersReducedMotion()) return;

  autoplayTimer = window.setInterval(() => {
    goTo((current.value + 1) % images.value.length);
  }, AUTOPLAY_INTERVAL_MS);
};

// Stops for good rather than pausing: once someone steers the slider themselves,
// having it move again under their hands is worse than it standing still. That
// action is also the stop mechanism WCAG 2.2.2 asks for.
const stopAutoplay = () => {
  autoplayTakenOver = true;
  clearAutoplay();
};

const goToManually = (index) => {
  stopAutoplay();
  goTo(index);
};

// Opened from a map popup the seed carries no images; those only arrive with the
// detail request, so autoplay has to start on that second render too.
watch(
  () => images.value.length,
  () => {
    if (state.isOpen) startAutoplay();
  }
);

const statusModifier = computed(() => ({
  'is-available': listing.value.status === 'Beschikbaar',
  'is-option': listing.value.status === 'In optie',
  'is-sold': listing.value.status === 'Verkocht'
}));

const title = computed(() => housingTitleWithNumber(listing.value));

const subtitle = computed(() => {
  const { project, subProject, city } = listing.value;
  const projectName = typeof project === 'object' && project !== null ? project.name : project;
  const phase = [projectName, subProject].filter(Boolean).join(' – ');
  return [phase, city].filter(Boolean).join(', ');
});

// mirrors ListingCard: v.o.n. always suffixes, indicative gets a prefix
const price = computed(() => {
  const { definitivePrice, indicativePrice } = listing.value;
  const amount = definitivePrice || indicativePrice;
  if (!amount) return { prefix: '', value: t('listing-card.price-on-request'), suffix: '' };
  return {
    prefix: definitivePrice ? '' : t('listing-card.price.indicative-prefix'),
    value: `€ ${priceFormatter.format(amount)}`,
    suffix: t('listing-card.price.suffix')
  };
});

// Field set, order and suffixes follow the detail page hero
// (project-page-hero-brick/variants/constructionNumber.html.twig), minus the
// price row that already sits above the buttons. Empty or 0 drops out, the
// same rule as infoBlock/info.html.twig.
const specs = computed(() => {
  const { housingKind, livingArea, parcelArea, energyLabel, bedrooms, layer } = listing.value;
  const detailSpecs = listing.value.specifications || {};

  // Icon names are the sprite symbols the construction-number page already uses
  // for these same specs (project-page-hero-brick/variants/constructionNumber),
  // so the panel and the detail page speak one visual language (WHN-2144).
  const rows = [
    ['listing-detail.specs.kind', housingKind, '', 'house'],
    ['listing-detail.specs.living', livingArea, ' m²', 'wonen'],
    ['listing-detail.specs.parcel', parcelArea, ' m²', 'perceel'],
    ['listing-detail.specs.energy', energyLabel, '', 'energielabel'],
    ['listing-detail.specs.orientation', detailSpecs.orientation, '', 'ligging'],
    ['listing-detail.specs.bedrooms', bedrooms, '', 'bedrooms'],
    ['listing-detail.specs.layer', layer, '', 'woonlagen'],
    ['listing-detail.specs.garden-depth', detailSpecs.gardenDepth, ' m', 'tuindiepte'],
    ['listing-detail.specs.outbuilding', detailSpecs.outbuilding, '', 'bijgebouw'],
    ['listing-detail.specs.parking', detailSpecs.parking, '', 'parking'],
    ['listing-detail.specs.volume', detailSpecs.volume, ' m³', 'inhoud-wonen'],
    [
      'listing-detail.specs.kitchen-cheque',
      detailSpecs.kitchenCheque ? `€ ${priceFormatter.format(detailSpecs.kitchenCheque)}` : null,
      '',
      'prijs-dynamic'
    ]
  ];

  // Number() mirrors Twig's loose `value != 0`: '0' drops out, 'N.W.' stays
  return rows
    .filter(([, value]) => value !== null && value !== undefined && value !== '' && Number(value) !== 0)
    .map(([key, value, suffix, icon]) => ({ label: t(key), value: `${value}${suffix}`, icon }));
});

const descriptionHtml = computed(() =>
  openLinksInNewTab(stripTextColors(sanitizeHtml(listing.value.description?.body)))
);

const focusables = () =>
  Array.from(
    panel.value?.querySelectorAll('a[href], button:not([disabled])') || []
  ).filter((el) => el.offsetParent !== null);

const onKeydown = (event) => {
  if (event.key === 'Escape') {
    close();
    return;
  }
  if (event.key !== 'Tab') return;

  const elements = focusables();
  if (!elements.length) return;
  const first = elements[0];
  const last = elements[elements.length - 1];

  if (event.shiftKey && (document.activeElement === first || !panel.value.contains(document.activeElement))) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
};

let previousOverflow = '';

const activate = () => {
  document.addEventListener('keydown', onKeydown);
  previousOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';
  current.value = 0;
  specsOpen.value = false;
  autoplayTakenOver = false;
  startAutoplay();
  nextTick(() => closeButton.value?.focus());
};

const deactivate = () => {
  clearAutoplay();
  document.removeEventListener('keydown', onKeydown);
  document.body.style.overflow = previousOverflow;
};

watch(
  () => state.isOpen,
  (isOpen) => (isOpen ? activate() : deactivate())
);

onBeforeUnmount(() => {
  if (state.isOpen) deactivate();
});
</script>
